/**
 * Share a secret with users and groups.
 *
 * Permission rules:
 * 1. Only users with `manage` on the secret see this dialog open to writes.
 *    The API enforces this; on the client we trust the 403 response.
 * 2. Subjects are always shown by their email (users) or name (groups),
 *    never by raw UUID (see SEC identity labeling rule).
 */

import { FormEvent, useMemo, useState } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { Trash2, UserPlus, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { useI18n } from '@/i18n';
import { useTenantMembers, type TenantMember } from '@/features/permissions/hooks/usePermissions';
import { useGroups } from '@/hooks/use-groups';
import {
  useAddShare,
  useRemoveShare,
  useSecretShares,
} from '../hooks/use-secrets';
import type { Grant, GrantPermission, GrantSubjectType, Secret } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  secret: Secret | null;
}

export interface SubjectOption {
  id: string;
  label: string;
}

/** ISS-649: solo miembros identificables (con email) y etiqueta = email. */
export function buildMemberOptions(items: TenantMember[]): SubjectOption[] {
  return items
    .filter((member) => Boolean(member.email))
    .map((member) => ({
      id: member.user_id,
      // IDENTITY LABELING RULE: email is the visible label, never UUID.
      label: member.email as string,
    }));
}

/** ISS-649: búsqueda del selector de usuarios (email/nombre, case-insensitive). */
export function filterSubjectOptions(
  options: SubjectOption[],
  subjectType: GrantSubjectType,
  query: string,
): SubjectOption[] {
  if (subjectType !== 'user' || !query.trim()) return options;
  const normalized = query.trim().toLowerCase();
  return options.filter((option) => option.label.toLowerCase().includes(normalized));
}

export function SecretShareModal({ open, onClose, secret }: Props) {
  const { t } = useI18n();
  const [subjectType, setSubjectType] = useState<GrantSubjectType>('user');
  const [subjectId, setSubjectId] = useState<string>('');
  const [permission, setPermission] = useState<GrantPermission>('use');
  const [userFilter, setUserFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const isDirty =
    open && (subjectId !== '' || subjectType !== 'user' || permission !== 'use');
  const discardLabels = {
    title: t('common.discardChanges.title') ?? 'Discard changes?',
    message: t('common.discardChanges.message') ?? 'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') ?? 'Discard',
    cancel: t('common.discardChanges.keepEditing') ?? 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  const shares = useSecretShares(open && secret ? secret.id : null);
  const membersQuery = useTenantMembers();
  const groupsQuery = useGroups();
  const addShare = useAddShare();
  const removeShare = useRemoveShare();

  const memberOptions = useMemo(() => {
    const items = membersQuery.data?.items ?? [];
    // ISS-649: solo miembros identificables (con email); las tuplas sin
    // identidad/metadata (“Email no disponible”) no se ofrecen para compartir.
    return buildMemberOptions(items);
  }, [membersQuery.data]);

  const groupOptions = useMemo(() => {
    const items = groupsQuery.data?.items ?? [];
    return items.map((group) => ({
      id: group.id,
      label: group.name,
    }));
  }, [groupsQuery.data]);

  const options = subjectType === 'user' ? memberOptions : groupOptions;

  // ISS-649: búsqueda del selector de usuarios (email/nombre).
  const filteredOptions = useMemo(
    () => filterSubjectOptions(options, subjectType, userFilter),
    [options, subjectType, userFilter],
  );

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!secret) return;
    setError(null);

    if (!subjectId) {
      setError(t('admin.secrets.share.errors.subjectRequired') || 'Select a subject to share with');
      return;
    }

    try {
      await addShare.mutateAsync({
        secretId: secret.id,
        input: { subject_type: subjectType, subject_id: subjectId, permission },
      });
      setSubjectId('');
      setPermission('use');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('admin.secrets.share.errors.generic') || 'Could not share secret';
      setError(message);
    }
  }

  function handleRemove(grant: Grant) {
    if (!secret) return;
    removeShare.mutate({
      secretId: secret.id,
      subjectType: grant.subject_type,
      subjectId: grant.subject_id,
      permission: grant.permission,
    });
  }

  function labelForGrant(grant: Grant): string {
    const pool = grant.subject_type === 'user' ? memberOptions : groupOptions;
    const found = pool.find((item) => item.id === grant.subject_id);
    if (found) return found.label;
    // Fallback keeps UUID out of the user-facing label.
    return grant.subject_type === 'user'
      ? t('admin.secrets.share.unknownUser') || 'Unknown user'
      : t('admin.secrets.share.unknownGroup') || 'Unknown group';
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : undefined)}>
      <DialogContent
        className="sm:max-w-xl"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle>
            {t('admin.secrets.share.title') || 'Share secret'}
          </DialogTitle>
          <DialogDescription>
            {secret
              ? secret.name
              : t('admin.secrets.share.description') ||
                'Grant users or groups access to use or manage this secret.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-3 space-y-1.5">
              <Label>{t('admin.secrets.share.subjectType') || 'Type'}</Label>
              <Select
                value={subjectType}
                onValueChange={(next) => {
                  setSubjectType(next as GrantSubjectType);
                  setSubjectId('');
                  setUserFilter('');
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    {t('admin.secrets.share.types.user') || 'User'}
                  </SelectItem>
                  <SelectItem value="group">
                    {t('admin.secrets.share.types.group') || 'Group'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-5 space-y-1.5">
              <Label>{t('admin.secrets.share.subject') || 'Subject'}</Label>
              {subjectType === 'user' && (
                <Input
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  placeholder={
                    t('admin.secrets.share.searchPlaceholder') ||
                    'Search by email or name…'
                  }
                  aria-label={
                    t('admin.secrets.share.searchPlaceholder') ||
                    'Search by email or name…'
                  }
                />
              )}
              <Select value={subjectId} onValueChange={setSubjectId}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      t('admin.secrets.share.subjectPlaceholder') || 'Select…'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {filteredOptions.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.label}
                    </SelectItem>
                  ))}
                  {filteredOptions.length === 0 && (
                    <p className="px-2 py-1.5 text-sm text-muted-foreground">
                      {t('admin.secrets.share.noMatches') || 'No matches'}
                    </p>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 space-y-1.5">
              <Label>{t('admin.secrets.share.permission') || 'Access'}</Label>
              <Select
                value={permission}
                onValueChange={(next) => setPermission(next as GrantPermission)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="use">
                    {t('admin.secrets.share.permissions.use') || 'Use'}
                  </SelectItem>
                  <SelectItem value="manage">
                    {t('admin.secrets.share.permissions.manage') || 'Manage'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 flex items-end">
              <Button type="submit" className="w-full" disabled={addShare.isPending}>
                <UserPlus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </form>

        <div className="border-t pt-3">
          <h4 className="text-sm font-medium mb-2">
            {t('admin.secrets.share.currentAccess') || 'Current access'}
          </h4>

          {shares.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner size="sm" /> {t('common.loading') || 'Loading…'}
            </div>
          ) : !shares.data || shares.data.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('admin.secrets.share.empty') || 'Only the owner currently has access.'}
            </p>
          ) : (
            <ul className="divide-y">
              {shares.data.data.map((grant) => (
                <li
                  key={`${grant.subject_type}:${grant.subject_id}:${grant.permission}`}
                  className="flex items-center gap-3 py-2"
                >
                  <span className="flex-1 flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {labelForGrant(grant)}
                  </span>
                  <Badge variant={grant.permission === 'manage' ? 'default' : 'secondary'}>
                    {grant.permission === 'manage'
                      ? t('admin.secrets.share.permissions.manage') || 'Manage'
                      : t('admin.secrets.share.permissions.use') || 'Use'}
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(grant)}
                    disabled={removeShare.isPending}
                    aria-label={t('admin.secrets.share.revoke') || 'Revoke'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}
